import React, { Component } from "react";
import { addMilliseconds, subMilliseconds } from "date-fns";
import { observable } from "mobx";
import { observer } from "mobx-react";
import { v4 as uuidv4 } from "uuid";

import { definedAndNotNull } from "@formant/core/common/defined";
import {
    AddableModuleType,
    isCustomModuleType,
} from "@formant/core/model/AddableModuleType";
import { duration } from "@formant/core/model/duration";
import { ICustomModuleParameters } from "@formant/core/model/ICustomModuleParameters";
import { StreamType } from "@formant/core/model/StreamType";
import { IconName } from "@formant/ui/component/common/Icon";

import {
    authStore,
    customModuleStore,
    metadataStore,
    observeStore,
    queryStore,
} from "../../../stores";
import { createCustomWebViewUrl } from "../../../utils/createCustomWebViewUrl";
import { Scrollable } from "../../common/Scrollable";
import { CustomModule } from "../../CustomModule";
import {
    IGoToTime,
    IRequestModuleData,
    ISetModuleTimeRange,
    ISetupModuleMenus,
} from "../../CustomViewMessages";
import { IBaseModuleProps } from "../IBaseModuleProps";
import { Module } from "../Module";
import * as styles from "./index.scss";

const bustValue = uuidv4();
interface ICustomVisualizationModuleProps extends IBaseModuleProps {
    moduleSchemaUrl?: string;
    moduleConfigurationId?: string;
}

const loadedUrls: string[] = [];

@observer
export class CustomVisualizationModule extends Component<ICustomVisualizationModuleProps> {
    @observable
    timeRange: { before: number; after: number } = {
        before: 15 * duration.minute,
        after: 0,
    };

    @observable
    streamTypes: { [key in string]: AddableModuleType } | undefined;

    customMenuItems?: { label: string; icon?: IconName; onClick: () => void }[];

    private customView = React.createRef<CustomModule>();

    @observable
    private authToken: string | undefined;

    loadCustomElement(url: string) {
        if (loadedUrls.includes(url)) {
            return;
        } else {
            loadedUrls.push(url);
        }
        const script = document.createElement("script");
        script.type = "module";
        script.src = url;
        script.async = true;
        document.head.appendChild(script);
    }

    async componentDidMount() {
        authStore
            .token()
            .then(_ => (this.authToken = _))
            .catch(err => console.error(err));

        if (this.props.layoutModule?.configuration?.streams) {
            const metadata = await metadataStore.listAsync({
                ...this.props.filter,
                names: this.props.layoutModule?.configuration?.streams,
            });
            if (metadata) {
                const streamTypes: { [key in string]: StreamType } = {};
                metadata.forEach(_ => {
                    streamTypes[_.name] = _.type;
                });
                this.streamTypes = streamTypes;
                this.sendData();
            }
        }
    }

    sendData() {
        const { filter, streamName, streamType } = this.props;
        const debug = this.props.layoutModule?.configuration?.debug;
        const { time } = observeStore;
        const streams: { [key in string]: any } = {};
        let loading;
        let tooMuchData;

        const end = addMilliseconds(time, this.timeRange.after);
        const start = subMilliseconds(time, this.timeRange.before);

        // block from cutsom module
        if (!isCustomModuleType(streamType)) {
            const queryData = queryStore.moduleQuery(
                filter,
                streamName,
                streamType as StreamType,
                start,
                end,
            );
            loading = queryData === undefined || this.authToken === undefined;
            tooMuchData = queryData === "too much data";
            streams[streamName] = {
                data: queryData !== "too much data" ? queryData : undefined,
                loading,
                tooMuchData,
                type: streamType,
            };
        } else if (
            streamType === "custom" &&
            this.props.layoutModule?.configuration?.streams
        ) {
            if (this.streamTypes) {
                const allStream =
                    this.props.layoutModule?.configuration?.streams || [];
                allStream.forEach(name => {
                    if (this.streamTypes && this.streamTypes[name]) {
                        const type = this.streamTypes[name];
                        if (!isCustomModuleType(type)) {
                            const queryData = queryStore.moduleQuery(
                                filter,
                                name,
                                type as StreamType,
                                start,
                                end,
                            );
                            loading =
                                queryData === undefined ||
                                this.authToken === undefined;
                            tooMuchData = queryData === "too much data";
                            streams[name] = {
                                data:
                                    queryData !== "too much data"
                                        ? queryData
                                        : undefined,
                                loading,
                                tooMuchData,
                                type,
                            };
                        }
                    }
                });
            }
        }

        if (this.customView && this.customView.current) {
            const data = {
                type: "module_data",
                streams,
                time: time.getTime(),
                queryRange: { start: start.getTime(), end: end.getTime() },
            };
            if (debug) {
                console.debug("Sending custom module data", {
                    data,
                });
            }
            this.customView.current.postMessage(data);
        }
    }

    onGoToTime = (msg: IGoToTime) => {
        observeStore.pauseAt(new Date(msg.time));
        this.sendData();
    };
    onRequestModuleData = (msg: IRequestModuleData) => {
        const { streamName } = this.props;
        if (msg.module === streamName) {
            this.sendData();
        }
    };
    onSetModuleTimeRange = (msg: ISetModuleTimeRange) => {
        const { streamName } = this.props;
        if (msg.module === streamName) {
            this.timeRange = { before: msg.before, after: msg.after };
            this.sendData();
        }
    };
    onSetupModuleMenus = (msg: ISetupModuleMenus) => {
        this.customMenuItems = msg.menus.map(_ => ({
            label: _.label,
            icon: _.icon,
            onClick: () => {
                const current = definedAndNotNull(this.customView.current);
                current.postMessage({
                    type: "module_menu_item_clicked",
                    menu: _.label,
                });
            },
        }));
    };

    render() {
        const { streamName, device, moduleSchemaUrl, moduleConfigurationId } =
            this.props;

        const customModuleDefinitionId =
            this.props.layoutModule?.configuration &&
            (
                this.props.layoutModule?.configuration.parameters as
                    | ICustomModuleParameters
                    | undefined
            )?.customModuleDefinitionId;

        const moduleConfiguration =
            customModuleDefinitionId &&
            customModuleStore.get(customModuleDefinitionId);

        if (moduleConfiguration && moduleConfiguration.isEmbedded) {
            const fileName = moduleConfiguration.url.split("/").pop();
            const fileNameWithoutExtension = fileName?.split(".").shift();
            if (
                fileName?.toLowerCase().endsWith(".js") &&
                fileNameWithoutExtension &&
                fileNameWithoutExtension.includes("-")
            ) {
                this.loadCustomElement(
                    moduleConfiguration.url + `?bust=${bustValue}`,
                );
                return (
                    <Module
                        {...this.props}
                        type={this.props.streamType}
                        className={styles["custom-visualization-module"]}
                        displayName={
                            this.props.layoutModule?.configuration?.customName
                        }
                        customMenuItems={this.customMenuItems}
                        icon={
                            moduleConfiguration &&
                            moduleConfiguration.iconImageUrl &&
                            moduleConfiguration.iconImageUrl.trim().length !==
                                0 && (
                                <img
                                    className={styles["custom-icon"]}
                                    src={moduleConfiguration.iconImageUrl}
                                />
                            )
                        }
                    >
                        <Scrollable>
                            {React.createElement(fileNameWithoutExtension, {
                                // attributes on html elements are lowercase
                                configuration: (
                                    this.props.layoutModule?.configuration
                                        ?.parameters as
                                        | ICustomModuleParameters
                                        | undefined
                                )?.customModuleConfigurationId,
                            })}
                        </Scrollable>
                    </Module>
                );
            }
        }

        this.sendData();

        return (
            <Module
                {...this.props}
                type={this.props.streamType}
                className={styles["custom-visualization-module"]}
                displayName={this.props.layoutModule?.configuration?.customName}
                customMenuItems={this.customMenuItems}
                icon={
                    moduleConfiguration &&
                    moduleConfiguration.iconImageUrl &&
                    moduleConfiguration.iconImageUrl.trim().length !== 0 && (
                        <img
                            className={styles["custom-icon"]}
                            src={moduleConfiguration.iconImageUrl}
                        />
                    )
                }
            >
                {this.props.layoutModule?.configuration
                    ?.customVisualizationUrl &&
                    this.authToken && (
                        <CustomModule
                            moduleSchemaUrl={moduleSchemaUrl}
                            moduleConfigurationId={moduleConfigurationId}
                            streamName={streamName}
                            module={streamName}
                            onGoToTime={this.onGoToTime}
                            onRequestModuleData={this.onRequestModuleData}
                            onSetModuleTimeRange={this.onSetModuleTimeRange}
                            onSetupModuleMenus={this.onSetupModuleMenus}
                            ref={this.customView}
                            className={styles.visualization}
                            src={createCustomWebViewUrl(
                                this.props.layoutModule?.configuration
                                    ?.customVisualizationUrl,
                                true,
                                device?.id,
                                this.authToken,
                                undefined,
                                undefined,
                                streamName,
                                (
                                    this.props.layoutModule?.configuration
                                        .parameters as
                                        | ICustomModuleParameters
                                        | undefined
                                )?.customModuleConfigurationId,
                            )}
                        />
                    )}
            </Module>
        );
    }
}
